import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { currencies } from '../data/currencyData';

const { FiCopy, FiCheckCircle, FiLoader, FiRefreshCw } = FiIcons;

const CurrencyConverter = ({ currencyRates, loading, onCopy, copiedValue }) => {
  const [amount, setAmount] = useState('100');
  const [fromCurrency, setFromCurrency] = useState('EUR');
  const [toCurrency, setToCurrency] = useState('USD');
  const [result, setResult] = useState(0);

  useEffect(() => {
    calculateConversion();
  }, [amount, fromCurrency, toCurrency, currencyRates]);

  const calculateConversion = () => {
    const value = parseFloat(amount) || 0;
    if (!currencyRates || Object.keys(currencyRates).length === 0) {
      setResult(0);
      return;
    }

    // Convert to EUR first (base currency), then to target currency
    const fromRate = fromCurrency === 'EUR' ? 1 : (currencyRates[fromCurrency] || 1);
    const toRate = toCurrency === 'EUR' ? 1 : (currencyRates[toCurrency] || 1);
    const eurAmount = value / fromRate;
    const convertedAmount = eurAmount * toRate;
    
    setResult(convertedAmount);
    
    // Track currency conversion for analytics
    if (value > 0 && window.gtag) {
      window.gtag('event', 'conversion', {
        'event_category': 'Unit Conversion',
        'conversion_id': 'currency_conversion',
        'unit_from': fromCurrency,
        'unit_to': toCurrency,
        'value': value
      });
    }
  };

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    
    // Track currency swap event
    if (window.gtag) {
      window.gtag('event', 'Swap Currencies', {
        event_category: 'Interaction',
        event_label: `${fromCurrency} ↔ ${toCurrency}`
      });
    }
  };

  const formatResult = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(value);
  };
  
  const handleAmountChange = (e) => {
    setAmount(e.target.value);
    
    // Track amount change (debounced to avoid too many events)
    if (e.target.value && parseFloat(e.target.value) > 0 && window.gtag) {
      window.gtag('event', 'Currency Amount Changed', {
        event_category: 'Interaction',
        event_label: fromCurrency,
        value: parseFloat(e.target.value)
      });
    }
  };
  
  const handleFromCurrencyChange = (e) => {
    const newCurrency = e.target.value;
    setFromCurrency(newCurrency);
    
    // Track currency change event
    if (window.gtag) {
      window.gtag('event', 'From Currency Changed', {
        event_category: 'Interaction',
        event_label: newCurrency
      });
    }
  };
  
  const handleToCurrencyChange = (e) => {
    const newCurrency = e.target.value;
    setToCurrency(newCurrency);
    
    // Track currency change event
    if (window.gtag) {
      window.gtag('event', 'To Currency Changed', {
        event_category: 'Interaction',
        event_label: newCurrency
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          Währungsrechner
        </h2>

        {loading && (
          <div className="text-center mb-6">
            <SafeIcon icon={FiLoader} className="text-2xl text-blue-600 animate-spin mx-auto" />
            <p className="text-gray-600 mt-2">Währungskurse werden geladen...</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Betrag
            </label>
            <input
              type="number"
              value={amount}
              onChange={handleAmountChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              placeholder="Betrag eingeben"
            />
          </div>

          {/* Currency Selection */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Von
              </label>
              <select
                value={fromCurrency}
                onChange={handleFromCurrencyChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              >
                {currencies.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.nameDe}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nach
              </label>
              <select
                value={toCurrency}
                onChange={handleToCurrencyChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              >
                {currencies.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.nameDe}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Swap Button */}
          <div className="text-center">
            <button
              onClick={swapCurrencies}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} />
              Währungen tauschen
            </button>
          </div>

          {/* Result */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600 mb-1">Umgerechneter Betrag</p>
                <p className="text-3xl font-bold text-gray-800">
                  {formatResult(result)} {toCurrency}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {amount} {fromCurrency} = {formatResult(result)} {toCurrency}
                </p>
                {currencyRates[toCurrency] && (
                  <p className="text-sm text-gray-500 mt-1">
                    1 {fromCurrency} = {formatResult(
                      fromCurrency === 'EUR'
                        ? currencyRates[toCurrency]
                        : currencyRates[toCurrency] / currencyRates[fromCurrency]
                    )} {toCurrency}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  onCopy(formatResult(result));
                  if (window.gtag) {
                    window.gtag('event', 'Copy Currency Result', {
                      event_category: 'Interaction',
                      event_label: `${fromCurrency} to ${toCurrency}`,
                      value: result
                    });
                  }
                }}
                className="text-green-600 hover:text-green-800 transition-colors"
              >
                <SafeIcon icon={copiedValue === formatResult(result) ? FiCheckCircle : FiCopy} className="text-2xl" />
              </button>
            </div>
          </motion.div>
        </div>

        {Object.keys(currencyRates).length > 0 && (
          <p className="text-xs text-gray-500 text-center mt-4">
            Kurse aktualisiert: {new Date().toLocaleString('de-DE')}
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default CurrencyConverter;